/* eslint-disable max-lines */
import React, { createRef, useEffect, useRef, useState } from 'react';
import { buildUniqueId, } from '@connectrn/ds-ui-base';
import { CFModalBottomSheet, CFModalContainer, CFModalWrapper, } from '../../components';
import { CFModalProviderContext } from './ModalProviderContext';
export const CFModalProvider = ({ children, footerSafeGap = 0, headerSafeGap = 0, onCloseModal, onDismissModals, onOpenModal, }) => {
    const [veilAlternateTheme, setVeilAlternateTheme] = useState(false);
    const [activeModal, setActiveModal] = useState(null);
    const modals = useRef([]);
    const [veilVisible, setVeilVisible] = useState(true);
    //
    // Context methods
    //
    // Add a modal by passing props consistent with the modal skin
    const addModal = ({ alternateTheme, onDismiss, preventDismiss, props, priority, ref, skin, veilVisible = true, }) => {
        // We buffer here to pass just props rather than a component
        configureModalInQueue({
            alternateTheme,
            component: null,
            id: props.id || buildUniqueId('cf-modal-'),
            onDismiss,
            preventDismiss,
            priority,
            props,
            ref: ref || createRef(),
            skin,
            veilVisible,
        });
    };
    // Add a modal by passing a complete component
    const addModalComponent = (component, options) => {
        const { alternateTheme = false, id = buildUniqueId('cf-modal-'), onDismiss, preventDismiss, priority = -1, veilVisible = true, } = options || {
            alternateTheme: false,
            id: buildUniqueId('cf-modal-'),
            preventDismiss: false,
            priority: -1,
            veilVisible: true,
        };
        // We buffer here to pass a component rather than just props
        configureModalInQueue({
            alternateTheme,
            component,
            id,
            onDismiss,
            preventDismiss,
            priority,
            props: null,
            ref: createRef(),
            veilVisible,
        });
    };
    // Dismiss a single modal if it is allowed to be dismissed
    const dismissAModal = (options) => {
        var _a;
        const { afterClose: onHide, checkForDismissHooks, onNoModal, } = options || {};
        const currentModal = modals.current[0] || {};
        const { id, onDismiss, preventDismiss, ref } = currentModal;
        // If `checkForDismissHooks` check for `preventDismiss` and/or `onDismiss`
        if (checkForDismissHooks) {
            if (preventDismiss)
                return false;
            if (onDismiss && id) {
                const dismissResults = onDismiss(id);
                if (dismissResults === false)
                    return false;
            }
        }
        // As long as there is a `close` method available for the active modal, close it
        if ((_a = ref.current) === null || _a === void 0 ? void 0 : _a.close) {
            modals.current.shift();
            ref.current.close({
                onHide,
            });
            return true;
        }
        if (onNoModal)
            onNoModal();
        return true;
    };
    // Disable only the current active modal
    // This leads to any following modal in the queue to trigger afterwards
    const dismissActiveModal = (options) => {
        if (modals.current.length < 1) {
            return;
        }
        return dismissAModal(Object.assign(Object.assign({}, options), { afterClose: () => configureNextModal(), onNoModal: () => configureNextModal() }));
    };
    // Dismiss active modal, then clear any modals in the queue
    const dismissAllModals = (options = {
        checkForDismissHooks: false,
    }) => {
        const dimissedSucceeded = dismissAModal(Object.assign(Object.assign({}, options), { afterClose: () => clearModals(), onNoModal: () => clearModals() }));
        // Check for overall `onDismissModals` hook
        if (dimissedSucceeded && onDismissModals)
            onDismissModals(modals.current.map(({ id }) => id));
    };
    //
    // Utilities
    //
    // Wipe out any queued modals
    const clearModals = () => {
        setActiveModal(null);
        setVeilVisible(true);
        modals.current = [];
    };
    // Ensure the new modal is place correctly in the queue if applicable
    const configureModalInQueue = (newModal) => {
        var _a;
        let newModals = [];
        const currentModals = modals.current;
        const { id, priority } = newModal;
        // Allows the use to provide a custom index
        // to add the new modal at a specific place in the queue
        // based on existing items' ids.
        if (typeof priority === 'function') {
            const queueIds = modals.current.map(({ id }) => id);
            const newIndex = priority(queueIds, id);
            if (newIndex === 0) {
                newModals = [newModal, ...currentModals];
            }
            else if (!newIndex ||
                newIndex === -1 ||
                newIndex > currentModals.length - 1) {
                newModals = [...currentModals, newModal];
            }
            else {
                newModals = [
                    ...currentModals.slice(0, newIndex),
                    newModal,
                    ...currentModals.slice(newIndex),
                ];
            }
        }
        // Otherwise we use the priority number:
        // - 1 adds at top of queue
        // - 0 adds behind top of queue
        // - -1 adds at tend of queue
        else {
            switch (priority) {
                case 1:
                    newModals = [newModal, ...currentModals];
                    break;
                case 0:
                    newModals =
                        currentModals.length === 0
                            ? [newModal]
                            : [currentModals[0], newModal, ...currentModals.slice(1)];
                    break;
                case -1:
                default:
                    newModals = [...currentModals, newModal];
                    break;
            }
        }
        modals.current = newModals;
        if (modals.current[0].id !== (activeModal === null || activeModal === void 0 ? void 0 : activeModal.id) &&
            ((_a = activeModal === null || activeModal === void 0 ? void 0 : activeModal.ref.current) === null || _a === void 0 ? void 0 : _a.close)) {
            activeModal.ref.current.close({
                onHide: configureNextModal,
            });
        }
        else {
            configureNextModal();
        }
    };
    // Configure states related to the next modal
    const configureNextModal = () => {
        if (modals.current.length > 0) {
            setActiveModal(modals.current[0]);
            setVeilAlternateTheme(modals.current[0].alternateTheme || false);
            setVeilVisible(modals.current[0].veilVisible);
        }
        else {
            // Setting null removes children from modal container
            // which results in it being removed from "DOM"
            setActiveModal(null);
            // Restore veil to standard true setting;
            // it will still be invisible because the container will hide altogether
            setVeilVisible(true);
        }
    };
    // Ensure the proper modal is rendered in modal container
    const renderActiveModal = () => {
        if (!activeModal)
            return null;
        const { alternateTheme, component, id, onDismiss, preventDismiss, props, ref, skin, } = activeModal;
        if (props) {
            switch (skin) {
                case 'bottom-sheet':
                default:
                    return (React.createElement(CFModalBottomSheet, Object.assign({ alternateTheme: alternateTheme, footerSafeGap: footerSafeGap, headerSafeGap: headerSafeGap, key: id }, props, { onClose: (id) => {
                            if (props.onClose)
                                props.onClose(id);
                            if (onCloseModal)
                                onCloseModal(id);
                        }, onHide: () => dismissAllModals(), onOpen: (id) => {
                            if (props.onOpen)
                                props.onOpen(id);
                            if (onOpenModal)
                                onOpenModal(id);
                        }, ref: ref })));
            }
        }
        if (component) {
            return (React.createElement(CFModalWrapper, { key: id, id: id, onClose: onCloseModal, onDismiss: (id) => {
                    if (onDismiss)
                        onDismiss(id);
                    dismissAllModals();
                }, onOpen: onOpenModal, preventDismiss: preventDismiss, ref: ref }, component));
        }
        return null;
    };
    //
    // Effects
    //
    // Watch active modal changes and ensure active modal is opened
    useEffect(() => {
        if (activeModal && activeModal.ref.current) {
            activeModal.ref.current.open();
        }
    }, [activeModal]);
    return (React.createElement(CFModalProviderContext.Provider, { value: {
            addModal,
            addModalComponent,
            dismissActiveModal,
            dismissAllModals,
            footerSafeGap: footerSafeGap !== null && footerSafeGap !== void 0 ? footerSafeGap : 0,
            headerSafeGap: headerSafeGap !== null && headerSafeGap !== void 0 ? headerSafeGap : 0,
        } },
        children,
        React.createElement(CFModalContainer, { alternateTheme: veilAlternateTheme, onDismiss: () => dismissAllModals({ checkForDismissHooks: true }), veilVisible: veilVisible }, renderActiveModal())));
};
